import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { backupService } from '@/services/backupService';
import { queryKeys } from '@/lib/queryKeys';
import { usePermissions } from './usePermissions';
import type {
  BackupPendingGroup,
  BackupPendingGroupFilters,
  MappaBackupPendingInput,
  Paginated,
  ScartaBackupPendingInput,
} from '@/types';

export function useBackupPendingGroups(
  filters: BackupPendingGroupFilters = {},
): UseQueryResult<Paginated<BackupPendingGroup>> {
  const { isStaff } = usePermissions();
  return useQuery({
    queryKey: queryKeys.backup.pending.groups(filters),
    queryFn: () => backupService.pendingGroups(filters),
    enabled: isStaff,
    meta: { resourceName: 'backup da mappare' },
  });
}

export function useBackupPendingCount(): UseQueryResult<{ count: number }> {
  const { isStaff } = usePermissions();
  return useQuery({
    queryKey: queryKeys.backup.pending.count,
    queryFn: () => backupService.pendingCount(),
    enabled: isStaff,
    staleTime: 15_000,
    meta: { resourceName: 'coda backup' },
  });
}

export function useMappaBackupPending(): UseMutationResult<
  { promoted: number; macchina_id: string },
  Error,
  MappaBackupPendingInput
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input) => backupService.mappaBatch(input),
    meta: { successMessage: 'Job associati alla macchina.' },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.backup.pending.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.backup.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.macchine.all });
    },
  });
}

export function useScartaBackupPending(): UseMutationResult<{ discarded: number }, Error, ScartaBackupPendingInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input) => backupService.scartaBatch(input),
    meta: { successMessage: 'Job scartati.' },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.backup.pending.all });
    },
  });
}

export function useRiprocessaBackupPending(): UseMutationResult<
  { scanned: number; promoted: number; unresolved: number; remaining: number },
  Error,
  void
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => backupService.riprocessa(),
    meta: { successMessage: 'Coda riprocessata.' },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.backup.pending.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.backup.all });
    },
  });
}
